import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { UserService } from 'src/app/common/sharedServices/user/user.service';
import { Allegato, Evento, EventoDelResponse, EventoResponse, EventoUpdResponse } from 'src/app/common/models/Evento';
import { EventiSqlAdapterService } from './eventi-sql-adapter.service';
import { lastValueFrom } from 'rxjs';
import { AppUser } from 'src/app/common/models/Utente';
import { ChecklistService } from './checklist.service';

@Injectable({
  providedIn: 'root'
})
export class EventiService {
  private readonly GET_EVENTI_ROUTE = '/immto_wsr_eventi_get';
  private readonly INSUPDDEL_EVENTO_ROUTE = '/immto_wsr_eventiall_insupddel';
  private readonly INSUPD_EVENTI_ROUTE = '/immto_wsr_eventi_insupd';
  private readonly DELETE_EVENTI_ROUTE = '/immto_wsr_eventi_delete';

  constructor(
    private http: HttpClient,
    private eventiOffline: EventiSqlAdapterService, // Aggiornamento dell'anagrafica
    private userService: UserService, // Sorgente dei dati dell'utente necessari per la chiamata
    private checklistService: ChecklistService
    ) { }

  /**
   * Cerca di fare l'upload dell'evento fornito. L'operazione può fallire se:
   * - Il server non è raggiungibile 
   * - Il server rifiuta l'operazione (viene ritornato false come esito)
   * @param evento 
   * @param isCreazione 
   * @returns true se l'operazione e' andata a buon fine
   * @throws un errore se l'operazione e' fallita
   */
  async uploadEvento(evento: Evento, isCreazione: boolean): Promise<any> {
    const userData = await this.userService.getDatiUtente();
    let completeUrl = `${userData.server}${this.INSUPD_EVENTI_ROUTE}`;

    /**
     * Salvo gli allegati in una variabile e li cancello dall'oggetto evento per inviarli solo in un secondo momento.
     */
    const allegati: Allegato[] = evento.allegati ?? [];
    evento.allegati = [];
    try {      
      /**
       * SDTwsrEventi chiede un array di eventi, anche se ne viene caricato uno alla volta.
       */
      const eventi: any[] = [];
      const {checklist, idEventoLocale, ...eventoSenzaChecklist} = evento;
      eventi.push(eventoSenzaChecklist);

      const body = {
        'aziCdazi': userData.aziCdazi,
        'token': userData.token,
        'codiceAddetto': userData.putSigla,
        'idIntervento': eventoSenzaChecklist.idIntervento,
        'progressivo': 0,
        'SDTwsrEventiJson': JSON.stringify(eventi),
        'inCreazione': isCreazione
      };
      
      const response = await lastValueFrom(this.http.post<EventoUpdResponse>(completeUrl, body));
      
      if (response && response.st == 0) {
        /**
         * In caso di inserimento evento, l'app comunica un idEvento fasullo, quindi occorre sostituirlo con quello vero
         */
        if (isCreazione) evento.idEvento = response.nuovoIdEvento;
          
        /**
         * Upload checklist se esistente
         */
        if (checklist) await this.checklistService.updateChecklistToServer(checklist, evento.idEvento, evento.codiceOperatore);

        /**
         * Upload allegati
         */
          // if (allegati.length > 0)
            await this.uploadAllegatiEvento(userData, evento, allegati);

        return true
      } else {
        throw new Error('Aggiornamento evento NON andato a buon fine');
      }
    } catch (error) {
      console.error(`Impossibile caricare l'evento ${evento.idEvento} su server. Da sincronizzare`, error);
      throw Error(`Impossibile caricare l'evento ${evento.idEvento} su server. Da sincronizzare`);
    } finally {
      evento.allegati = allegati;
    }
  }

  private async uploadAllegatiEvento(userData: AppUser, evento: Evento, allegati: Allegato[]): Promise<any>{
    let completeUrl = `${userData.server}${this.INSUPDDEL_EVENTO_ROUTE}`;
    /**
     * Object destructuring assignment: 
     * - estraggo contenutoBase64Compresso e creo una copia di allegati senza quella proprietà;
     * - modifico contenutoBase64: elimino intestazione e sostituisco tutti gli 'spazi' (\\s) con dei '+' se presenti.
     */
    const allegatiSenzaCompresso = allegati.map(allegato => {
      const { contenutoBase64Compresso, contenutoBase64, ...allegatoSenzaCompresso } = allegato;

      // const nuovoContenutoBase64 = contenutoBase64.split(";base64,")[1].replace(/\\s/, "+");
      
      const nuovoContenutoBase64 = contenutoBase64
        ? (contenutoBase64.split(';base64,')[1]?.replace(/\s/g, '+') ?? '')
        : '';
      
      /**
       * Col metodo sottostante viene restituito un oggetto allegato con contenutoBase64 compresso,
       * ma non viene ancora utilizzato dato che continuerebbe a comprimere a ogni salvataggio.
       */
      // const nuovoContenutoBase64 = contenutoBase64Compresso
      //   ? (contenutoBase64Compresso.split(';base64,')[1]?.replace(/\s/g, '+') ?? '')
      //   : '';

      return { ...allegatoSenzaCompresso, contenutoBase64: nuovoContenutoBase64 };
    });
    
    const body = {
      'aziCdazi': userData.aziCdazi,
      'token': userData.token,
      'codiceOperatore': userData.putSigla,
      'idIntervento': evento.idIntervento,
      'progressivo': 0,
      'idEvento': evento.idEvento,
      'SDTwsrEventiAllegatiJson': JSON.stringify(allegatiSenzaCompresso)
    };

    try{
      const response = await lastValueFrom(this.http.post<EventoUpdResponse>(completeUrl, body));
      if (response && response.st == 0)
        return true 
      else 
        throw new Error('Aggiornamento allegati evento NON andato a buon fine');
    } catch (error) {
      console.error(`Impossibile caricare gli allegati per l'evento ${evento.idEvento} su server. Da sincronizzare`, error);
      throw Error(`Impossibile caricare gli allegati per l'evento ${evento.idEvento} su server. Da sincronizzare`);
    }
  }

  /**
   * Cerca di fare rimuovere l'evento fornito. L'operazione può fallire se:
   * - Il server non è raggiungibile 
   * - Il server rifiuta l'operazione (viene ritornato false come esito)
   * 
   * Va invocato solamente su eventi gia' presenti a DB 
   * 
   * @param evento 
   * @param isCreazione 
   * @returns true se l'operazione e' andata a buon fine
   * @throws un errore se l'operazione e' fallita
   */
  async cancellaEvento(evento: Evento): Promise<any> {
    const userData = await this.userService.getDatiUtente();
    const completeUrl = `${userData.server}${this.DELETE_EVENTI_ROUTE}`;

    try {
      const body = {
        aziCdazi: userData.aziCdazi,
        token: userData.token,
        idEvento: evento.idEvento
      };
      const response = await lastValueFrom(this.http.post<EventoDelResponse>(completeUrl, body));
      if (!response || response.st != 0) {
        throw Error(`Il server ha rifiutato la cancellazione dell' evento`);
      }
      return true;
    } catch (error) {
      console.error(`Impossibile cancellare l'evento ${evento.idEvento} su server. Da sincronizzare`, error);
      throw Error(`Impossibile cancellare l'evento ${evento.idEvento} su server. Da sincronizzare`);
    }
  }

  /**
   * Scarica l'anagrafica elenco eventi da web service e
   * li per tutti gli interventi forniti e ritorna il 
   * numero totale di eventi scaricati
   * 
   * Come effetto collaterale sovrascrive lo stato del
   * db degli eventi
   */
  async downloadAnagraficaEventi(idInterventi: number[]): Promise<number> {
    let numeroEventiScaricati = 0;
    await this.eventiOffline.rimuoviEventi();
    for (const idIntervento of idInterventi) {
      try {
        const eventi = await this.fetchEventiFromServer(idIntervento, 'E');
        if (eventi && eventi.length > 0) {
          this.eventiOffline.inserisciEventi(eventi);
        }
        numeroEventiScaricati += eventi.length;
      } catch (error) {
        console.error(`Si e' verificato un errore facendo il DOWNLOAD degli eventi dell'intervento ${idIntervento} da server`, error);
        return 0;
      }
    }
    console.debug(`Scaricati ${numeroEventiScaricati} eventi per tutti gli interventi disponibili`);
    return numeroEventiScaricati;
  }

  async fetchEventiOnline(idIntervento: number, tipoEntita: string): Promise<Evento[]> {
    try {
      const eventi = await this.fetchEventiFromServer(idIntervento, tipoEntita);
      return eventi;
    } catch (error) {
      console.error(`Si e' verificato un errore facendo il FETCH degli eventi da server`, error);
      return [];
    }
  }

  async fetchEventoOnline(idIntervento: number, idEvento: number): Promise<Evento> {
    try {
      const eventi = await this.fetchEventiFromServer(idIntervento, 'E', idEvento);
      return eventi ? eventi[0] : null;
    } catch (error) {
      console.error(`Si e' verificato un errore ottenendo il DETTAGLIO dell'evento con id ${idEvento}`, error);
      return null;
    }
  }

  private async fetchEventiFromServer(idIntervento: number, tipoEntita?: string, idEvento?: number, numeroPagina = 1): Promise<Evento[]> {
    const userData = await this.userService.getDatiUtente();
    let completeUrl = `${userData.server}${this.GET_EVENTI_ROUTE}`;

    const body = {
      aziCdazi: userData.aziCdazi,
      token: userData.token,
      idIntervento: idIntervento,
      progressivo: 0,
      idEvento: idEvento || 0,
      tipoEntita: tipoEntita,
      ordineDecrescente: 0,
      numeroPaginaCorrente: numeroPagina,
      righePerPag: 1000 //di fatto senza paginazione
    }
    
    const response = await lastValueFrom(this.http.post<EventoResponse>(completeUrl,body));
    /* Per ogni evento scaricato, scarico anche la rispettiva checklist se esistente */
    if (response.st == 0 && response.SDTwsrEventi)
      if (tipoEntita == 'E') 
        return await this.fetchChecklist(response.SDTwsrEventi) 
      else
        return response.SDTwsrEventi
    else
     return []
  }

  private async fetchChecklist(eventi: Evento[]): Promise<Evento[]>{
    await Promise.all(eventi.map(async e => {
      e.checklist = await this.checklistService.getChecklistFromServer(e.codiceAttivita, e.idEvento) ?? null;
    }));

    return eventi;
  }
}